import React from 'react';

import { useFormField } from '@lumapps/lumx-forms/hooks/useFormField';
import { BaseInput } from '@lumapps/lumx-forms/types';
import { Emphasis } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { LanguageSelectButton, LanguageSelectButtonProps } from '../LanguageSelectButton/LanguageSelectButton';

export type FormLanguageSelectButtonProps = Pick<
    LanguageSelectButtonProps,
    'defaultLanguage' | 'languages' | 'className' | 'shouldAdaptToScreen' | 'isDisabled' | 'placement'
> &
    Omit<BaseInput, 'name'> & {
        name?: string;
    };

/**
 * Component to be used for selecting a language in the context of a multilanguage form
 * @family Forms
 * @param FormLanguageSelectButtonProps
 * @returns FormLanguageSelectButton
 */
export const FormLanguageSelectButton: React.FC<FormLanguageSelectButtonProps & Omit<BaseInput, 'name'>> = ({
    defaultLanguage,
    name = 'lang',
    controllerProps,
    languages,
    isRequired,
    isDisabled,
    className,
    shouldAdaptToScreen = true,
    placement,
}) => {
    const { translateKey } = useTranslate();
    const { field, isFormLoading, isFieldDisabled, valueToUse, scope } = useFormField({
        name,
        isRequired,
        controllerProps: {
            ...controllerProps,
            defaultValue: defaultLanguage,
        },
        label: translateKey(GLOBAL.LANGUAGE),
    });

    return (
        <LanguageSelectButton
            className={className}
            defaultLanguage={valueToUse}
            inputLanguage={defaultLanguage}
            scope={scope}
            isDisabled={isFieldDisabled || isDisabled || isFormLoading}
            onChange={field.onChange}
            languages={languages}
            shouldAdaptToScreen={shouldAdaptToScreen}
            emphasis={Emphasis.medium}
            placement={placement}
        />
    );
};
